import { TFunction } from 'react-i18next';
import { DropdownItem } from '../pages/wechooose/OrderHistory/Filter';
import { Row } from '../pages/wechooose/OrderHistory/OrderHistory';

export function getDepartments(customMetadatas: CustomMetadata[]): string[] {
  const departments = customMetadatas.find(c => c.internalName === 'department')?.listValues ?? [];
  return [...departments].sort((a, b) => a.localeCompare(b));
}

export function getCountries(allCountries: NormalizedCountry[], customMetadatas: CustomMetadata[]): Option[] {
  const countries =
    customMetadatas
      .find(c => c.internalName === 'country')
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ?.listValues?.map(c => allCountries.find(a => a.value?.toLocaleLowerCase() === c?.toLocaleLowerCase())!) ?? [];
  return [...countries].sort((a, b) => a.label.localeCompare(b.label));
}

export function getCountryFromCustomMetadata(customMetadata?: OrderCustomMetadata[]): string {
  return customMetadata?.find(c => c.internalName === 'country')?.value ?? 'No country';
}

export function getDepartmentFromCustomMetadata(customMetadata?: OrderCustomMetadata[]): string {
  return customMetadata?.find(c => c.internalName === 'department')?.value ?? 'No department';
}

export function getCountryListFromOrders(orders: Row[]): DropdownItem[] {
  const defaultOrderCustomMetadata = [
    {
      internalName: '',
      value: '',
    },
  ];

  const countries = orders.map(order => getCountryFromCustomMetadata(order?.metadata?.custom ?? defaultOrderCustomMetadata));
  const nonDuplicateCountries = Array.from(new Set(countries));

  return nonDuplicateCountries.map(item => {
    return {
      label: item,
      value: item,
    };
  });
}

export function isMetadataSupported(account: Account | undefined, metadata: string): boolean {
  if (!account || !account.customer) {
    return false;
  }
  return account.customer.customMetadata?.findIndex(c => c.internalName === metadata) > -1 ?? false;
}

export const getCountryName = (countries: NormalizedCountry[], code: string): string => {
  return countries.find(c => c.value === code)?.label ?? '';
};

export const getCountriesMetadata = (t: TFunction<'settings'>, listValues?: string[], isMandatory?: boolean): CustomMetadata => {
  return {
    internalName: 'country',
    displayName: t('metadata.countries'),
    type: 'countryList',
    listValues: listValues ?? [],
    isMandatory: isMandatory ?? false,
  };
};

export const getDepartmentsMetadata = (t: TFunction<'settings'>, listValues?: string[], isMandatory?: boolean): CustomMetadata => {
  return {
    internalName: 'department',
    displayName: t('metadata.departments'),
    type: 'list',
    listValues: listValues ?? [],
    isMandatory: isMandatory ?? false,
  };
};

export const getDescriptionsMetadata = (t: TFunction<'settings'>, isMandatory?: boolean): CustomMetadata => {
  return {
    internalName: 'description',
    displayName: t('metadata.orderDescription'),
    type: 'text',
    isMandatory: isMandatory ?? false,
  };
};

export const getEmissionScopeMetadata = (t: TFunction<'settings'>, isMandatory?: boolean): CustomMetadata => {
  return {
    internalName: 'emissionScope',
    displayName: t('metadata.emissionScope'),
    type: 'list',
    listValues: [t('metadata.scopeDescription1'), t('metadata.scopeDescription2'), t('metadata.scopeDescription3')],
    isMandatory: isMandatory ?? false,
  };
};

export const getTimeFromMetadata = (t: TFunction<'settings'>, isMandatory?: boolean): CustomMetadata => {
  return {
    internalName: 'timeFrameFrom',
    displayName: t('metadata.timeFrameFrom'),
    type: 'text',
    isMandatory: isMandatory ?? false,
  };
};

export const getTimeToMetadata = (t: TFunction<'settings'>, isMandatory?: boolean): CustomMetadata => {
  return {
    internalName: 'timeFrameTo',
    displayName: t('metadata.timeFrameTo'),
    type: 'text',
    isMandatory: isMandatory ?? false,
  };
};

export const preDefinedMetadatasNames = ['country', 'department', 'description', 'emissionScope', 'timeFrameFrom', 'timeFrameTo'];

export const getCustomMetadata = (metadatas: CustomMetadata[]): CustomMetadata[] =>
  metadatas?.filter(({ internalName }) => !preDefinedMetadatasNames.includes(internalName)) ?? [];

export const getMandatoryCustomMetadata = (metadatas: CustomMetadata[]): CustomMetadata[] => {
  const customMetadata = getCustomMetadata(metadatas);
  return customMetadata.filter(({ isMandatory }) => isMandatory);
};

export const getOptionalCustomMetadata = (metadatas: CustomMetadata[]): CustomMetadata[] => {
  const customMetadata = getCustomMetadata(metadatas);
  return customMetadata.filter(({ isMandatory }) => !isMandatory);
};

export const getDisplayName = (metadata: CustomMetadata[], internalName: string): string => {
  return metadata.find(m => m.internalName === internalName)?.displayName ?? '';
};
